<template>
  <div>
    <formDialog
      v-if="ready"
      :isReadOnly="currentUser.role !== 'Verifier'"
      :applicant="applicantDetail"
      :qualifications="qualifications"
      @update="onSaved"
    />
    <v-overlay v-else>
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-overlay>

    <!-- Confirmation dialog shown when another user already owns this application -->
    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <confirmationDialogForm
        v-if="confirmationDialog"
        :confirmationText="confirmationText"
        :disableButton="disableProceed"
        @closeDialog="goBack"
        @saveDialog="proceedAfterConfirm"
      />
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/applicant/form.vue";
import confirmationDialogForm from "@/components/confirmationDialog.vue";
import { applicantPayload } from "@/helpers/payload";

const EDIT_STATE_KEY = "report_edit_state";

export default {
  name: "ReportEditView",
  components: { formDialog, confirmationDialogForm },

  data() {
    return {
      ready: false,
      applicantDetail: {},
      qualifications: [],
      confirmationDialog: false,
      confirmationText: "",
      disableProceed: false,
      // Store the applicant_detail id directly — no nested .applicant access needed
      pendingApplicantDetailId: null,
      fromDashboard: false,
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },

  async created() {
    // Read state from sessionStorage — avoids Vue Router query string coercion
    const raw = sessionStorage.getItem(EDIT_STATE_KEY);
    let editState = {};
    if (raw) {
      try {
        editState = JSON.parse(raw);
      } catch (e) {
        console.warn("Failed to parse edit state from sessionStorage", e);
      }
    }

    this.fromDashboard = Boolean(editState.fromDashboard);

    this.$store.commit("setBreadcrumbs", [
      { text: "Operational Dashboard", to: { name: "Operational Dashboard" } },
      {
        text: editState.headerValue || editState.headerKey || "List",
        to: { name: "Operational Dashboard List" },
      },
      { text: editState.applicationId || "Edit" },
    ]);

    await this.loadAndGuard(editState);
  },

  methods: {
    async loadAndGuard(editState) {
      const { id, programId } = editState;

      if (!id || !programId) {
        this.goBack();
        return;
      }

      try {
        const [programRes, applicantRes] = await Promise.all([
          this.$axios.get(`/programs/${programId}`),
          this.$axios.get(`/applicant_details/${id}`),
        ]);

        const applicantData = applicantRes.data.data;

        this.qualifications = programRes.data.data.qualifications;
        this.applicantDetail = applicantPayload(
          this.qualifications,
          applicantData,
        );

        // Store the applicant_detail id directly — the API endpoint takes this id,
        // NOT a nested applicantData.applicant.id (which doesn't exist on this response)
        this.pendingApplicantDetailId = applicantData.id;

        // Verifiers don't claim reviewer ownership — they act via the
        // verify_applicant endpoint, not verify_review_applicant — so skip
        // the reviewed_user_id assignment/ownership guards entirely for them.
        if (this.currentUser.role === "Verifier") {
          this.ready = true;
          return;
        }

        // Guard: no reviewer assigned yet → silently assign then open form
        if (!applicantData.reviewed_user_id) {
          await this.updateAdminId(applicantData.id);
          this.ready = true;
          return;
        }

        // Guard: different user owns it and it's not finalised
        if (
          this.currentUser.id !== applicantData.reviewed_user_id &&
          applicantData.application_status !== "Approved" &&
          applicantData.application_status !== "Admitted"
        ) {
          this.confirmationText =
            "You are about to review the applicant form. Reviewing will assign it to you. Are you sure you want to proceed?";
          this.confirmationDialog = true;
          return;
        }

        this.ready = true;
      } catch (e) {
        console.error("Failed to load applicant:", e);
        this.goBack();
      }
    },

    async proceedAfterConfirm() {
      this.confirmationDialog = false;
      if (this.pendingApplicantDetailId) {
        await this.updateAdminId(this.pendingApplicantDetailId);
      }
      this.ready = true;
    },

    async updateAdminId(applicantDetailId) {
      try {
        await this.$axios.put(`/verify_review_applicant/${applicantDetailId}`, {
          type: "Review",
        });
      } catch (err) {
        const msg = await this.$store.dispatch("throwError", err.response);
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      }
    },

    onSaved() {
      this.goBack();
    },

    goBack() {
      if (this.fromDashboard) {
        this.$router.push({ name: "Operational Dashboard" });
      } else {
        this.$router.go(-1);
      }
    },
  },
};
</script>
